import axios, { type AxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../config";
import { useAppStore } from "../store/appStore";

const ACCESS_KEY = "lk_ops_access";
const REFRESH_KEY = "lk_ops_refresh";

const accessToken = () => localStorage.getItem(ACCESS_KEY);
const refreshToken = () => localStorage.getItem(REFRESH_KEY);

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Separate instance for /auth/refresh: the response interceptor on `api`
// retries 401s by calling doRefresh(). Routing the refresh call through `api`
// would make it await its own promise — a circular wait that hangs the UI on
// a spinner instead of logging the user out.
const refreshApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const t = accessToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  const tenantId = useAppStore.getState().tenantId;
  if (tenantId) config.headers["X-Tenant-Id"] = tenantId;
  return config;
});

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

/** Call /auth/refresh and store the new tokens. Returns the new access token. */
export async function doRefresh(): Promise<string> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  const rt = refreshToken();
  if (!rt) throw new Error("No refresh token");
  isRefreshing = true;
  refreshPromise = refreshApi
    .post<RefreshResponse>("/auth/refresh", { refresh_token: rt })
    .then((res) => {
      localStorage.setItem(ACCESS_KEY, res.data.access_token);
      localStorage.setItem(REFRESH_KEY, res.data.refresh_token);
      return res.data.access_token;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  return refreshPromise;
}

/** Clear tokens and redirect to login. */
export function forceLogout() {
  // Revoke the refresh token server-side before dropping it — best effort,
  // the session is dead locally either way. Goes through refreshApi because
  // `api`'s 401 interceptor could recurse back into forceLogout.
  const rt = refreshToken();
  if (rt) refreshApi.post("/auth/logout", { refresh_token: rt }).catch(() => {});
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  if (!window.location.pathname.startsWith("/ops/login")) {
    window.location.replace("/ops/login");
  }
}

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

api.interceptors.response.use(
  (r) => r,
  async (err: unknown) => {
    if (!axios.isAxiosError(err)) return Promise.reject(err);
    const original = err.config as RetriableConfig | undefined;
    if (!original) return Promise.reject(err);
    if (err.response?.status === 401 && !original._retried) {
      try {
        const newToken = await doRefresh();
        original._retried = true;
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        forceLogout();
      }
    } else if (err.response?.status === 401) {
      forceLogout();
    }
    return Promise.reject(err);
  }
);
